import React, { FC, useContext, useState } from 'react';
import { UseFieldArrayAppend } from 'react-hook-form';

import { AttachmentGroups } from 'buyside/components/presentation/IdeaForm/WriteupAndAttachments/writeupAndAttachments';
import { IdeaFormContext } from 'buyside/components/presentation/IdeaForm/ideaForm';
import ElevatorPitchField from 'buyside/components/presentation/ResearchFormWizard/SharedFields/elevatorPitchField';
import MentionedEntitiesField from 'buyside/components/presentation/ResearchFormWizard/SharedFields/mentionedEntitiesField';
import ResearchFormWizardSection from 'buyside/components/presentation/ResearchFormWizard/researchFormWizardSection';
import TagListField from 'buyside/components/presentation/ResearchFormWizard/SharedFields/tagListField';
import ThesisField from 'buyside/components/presentation/ResearchFormWizard/SharedFields/thesisField';
import ThesisTypeSelection from 'buyside/components/presentation/IdeaForm/WriteupAndAttachments/Writeup/thesisTypeSelection';
import WriteupAlerts from 'buyside/components/presentation/IdeaForm/WriteupAndAttachments/Writeup/writeupAlerts';

import { IdeaFormInput } from 'buyside/util/researchSubmission';

import styles from './writeupStyles.scss';

const Writeup: FC<{
  append: UseFieldArrayAppend<IdeaFormInput, 'attachments'>;
  handleRemoveFormattedThesisAttachment: () => void;
  handleRemoveThesisAttachment: () => void;
  setThesisEditorHidden: (hidden: boolean) => void;
  thesisAttachmentGroup: AttachmentGroups['Thesis'];
  thesisEditorHidden: boolean;
}> = ({
  append,
  handleRemoveFormattedThesisAttachment,
  handleRemoveThesisAttachment,
  setThesisEditorHidden,
  thesisAttachmentGroup,
  thesisEditorHidden
}) => {
  const { ideaId, isDraft } = useContext(IdeaFormContext);

  const [writeupFieldsHidden, setWriteupFieldsHidden] = useState(!isDraft);

  if (!ideaId) throw new Error();

  return (
    <ResearchFormWizardSection title="Writeup">
      {writeupFieldsHidden ? (
        <WriteupAlerts ideaId={ideaId} setWriteupFieldsHidden={setWriteupFieldsHidden} />
      ) : (
        <>
          <p>
            Please provide a detailed write-up on what makes this an attractive investment. In
            addition to an overview of the security, write-ups should include a discussion of
            valuation, catalysts, and risks. The average report on SumZero is over 1,800 words long,
            and a minimum of 1000 words is required. The highest-rated reports on SumZero often
            identify hard catalysts and offer deep insights into the opportunity supported by direct
            evidence and citations. Ideas deemed insufficiently detailed may be removed from the
            site. Users who submit incomplete ideas may be asked to provide more content to the
            write-up.
          </p>
          <div className={styles.gridLayout}>
            <ElevatorPitchField />
          </div>
          {thesisEditorHidden ? (
            <ThesisTypeSelection
              append={append}
              handleRemoveFormattedThesisAttachment={handleRemoveFormattedThesisAttachment}
              handleRemoveThesisAttachment={handleRemoveThesisAttachment}
              setThesisEditorHidden={setThesisEditorHidden}
              thesisAttachmentGroup={thesisAttachmentGroup}
            />
          ) : (
            <ThesisField
              imageableId={ideaId}
              imageableType="Idea"
              onAttachThesisInstead={() => {
                setThesisEditorHidden(true);
                handleRemoveFormattedThesisAttachment();
              }}
            />
          )}
          <div className={styles.gridLayout}>
            <TagListField />
            <MentionedEntitiesField />
          </div>
        </>
      )}
    </ResearchFormWizardSection>
  );
};

export default Writeup;
